import { ConfirmPopup, type ConfirmPopupProps } from '@neo/blocks/confirm-popup'
import { CxDataTable } from '@neo/blocks/cx-data-table/components/cx-data-table'
import { FacetedFilter } from '@neo/blocks/faceted-filter'
import { cn } from '@neo/lib/utils'
import { Typography } from '@neo/primitives/typography'
import { ReactComponent as IcIntegrationBadge } from '@shared/assets/icons/ic-integration-badge.svg'
import { orderBy } from 'lodash'
import { Plus } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { QueryError } from 'v2/api-console/shared/components/query-error'
import { IntegrationDetailSheet } from 'v2/pages/settings/integrations/apps/integration-detail-sheet'
import { IntegrationSideSheet } from 'v2/pages/settings/integrations/apps/integration-side-sheet'
import type { ToolMenuAction } from 'v2/pages/settings/integrations/apps/tool-actions-menu'
import {
  invalidateExternalAppQueries,
  useDeleteOrgAppConnectionMutation,
  useExternalOrgAppsQuery,
  useRevokeAllAccessMutation,
  useUpdateOrgAppConnectionMutation,
} from 'v2/pages/settings/integrations/queries'
import { useUsersQuery } from 'v2/pages/settings/queries'
import { ButtonWithPermission } from 'v2/shared/components/button-with-permission'
import { useAuth } from 'v2/shared/hooks/use-auth'
import { usePermissions } from 'v2/shared/hooks/use-permissions'
import type { RExternalOrgApp } from 'v2/types/api/settings'
import {
  type IntegrationErrorEventData,
  type IntegrationSuccessEventData,
  PubnubEvents,
} from 'v2/types/pubnub'
import { RbacModules, RbacPermissionTypes } from 'v2/types/rbac'
import { pubnubEventBus } from 'v2/utils/event-bus/pubnub'
import { errorToast, generateToastsForMutation, successToast } from 'v2/utils/toasts'
import { IntegrationState } from '../utils/constants'
import { CustomFieldsDialog } from './custom-fields-dialog'
import type { ExternalAppRow } from './external-app-actions'
import { buildGroupedTableRows, getColumns, integrationStatusOptions } from './external-apps.config'
import { connections } from './external-apps-rollup'
import ShareConnectionButton from './share-connection/share-connection-button'

const ExternalApps = () => {
  const [isSideSheetOpen, setIsSideSheetOpen] = useState(false)
  const [statusFilter, setStatusFilter] = useState<string[]>([])
  const [popupState, setPopupState] = useState<ConfirmPopupProps>({})
  const [customFieldsApp, setCustomFieldsApp] = useState<RExternalOrgApp | null>(null)
  const [detailApp, setDetailApp] = useState<ExternalAppRow | null>(null)
  const openDetailRow = useRef<ExternalAppRow | null>(null)
  // Owned at page level: the detail sheet closes before sharing opens and would unmount it.
  const [sharingApp, setSharingApp] = useState<ExternalAppRow | null>(null)
  const isAnyPopupOpen = popupState.open || false

  const { data: apps = [], isLoading, isError, refetch } = useExternalOrgAppsQuery()
  const [isRetrying, setIsRetrying] = useState(false)
  const { mutate: updateOrgAppConnection } = useUpdateOrgAppConnectionMutation()
  const { mutate: deleteOrgAppConnection } = useDeleteOrgAppConnectionMutation()
  const { mutate: revokeAllAccess } = useRevokeAllAccessMutation()
  const { details: currentUser } = useAuth()
  const { hasPermission } = usePermissions()

  // The ACI bridge stores only the owner's aom-id on a connection (no CX user directory),
  // so resolve the owner's display name from the org user list here (same source the share
  // dialog uses). This drives the Owner avatar + the account-identifier fallback label.
  const { data: usersData } = useUsersQuery()
  const userMap = useMemo(() => {
    const m: Record<string, string> = {}
    for (const u of usersData?.members_list ?? []) m[u.aom_uuid] = u.account_name
    return m
  }, [usersData])

  // Business logic handlers for context menu actions
  const handleToggleState = useCallback(
    (app: ExternalAppRow) => {
      const state = !(
        app?.state === IntegrationState.ACTIVE || app?.state === IntegrationState.EXPIRED
      )
      const payload = {
        app_uuid: app?.id,
        connection_details: app?.connection_details,
        enabled: state,
        priority_order: app?.priority,
      }
      updateOrgAppConnection(
        { payload, appId: app.id },
        generateToastsForMutation(
          {
            title: 'Success',
            description: `Successfully ${state ? 'enabled' : 'disabled'} the app`,
            callback: () => {
              setPopupState({ open: false })
            },
          },
          { title: 'Error', description: 'Failed to update app' },
        ),
      )
    },
    [updateOrgAppConnection],
  )

  const handleDelete = useCallback(
    (app: ExternalAppRow) => {
      deleteOrgAppConnection(
        { appId: app.id },
        generateToastsForMutation(
          {
            title: 'Success',
            description: `Successfully deleted the ${app.name} app`,
            callback: () => {
              setPopupState({ open: false })
            },
          },
          { title: 'Error', description: `Failed to delete the ${app.name} app` },
        ),
      )
    },
    [deleteOrgAppConnection],
  )

  const handleCustomFields = useCallback((app: ExternalAppRow) => {
    setCustomFieldsApp(app)
  }, [])

  // `targets` is every connection under the tool. `ToolActionsMenu` only offers Enable/Disable
  // when all of them agree, so the confirm label is true of each.
  const handleToolAction = useCallback(
    (action: ToolMenuAction, targets: ExternalAppRow[], closeSheetOnDelete = true) => {
      if (targets.length === 0) return
      const accounts = targets.length > 1 ? ` and its ${targets.length} connected accounts` : ''

      if (action === 'delete') {
        setPopupState({
          open: true,
          title: 'Delete App',
          description: `Are you sure you want to delete "${targets[0].name}"${accounts}?`,
          okButtonProps: {
            variant: 'destructive',
            onClick: () => {
              for (const target of targets) handleDelete(target)
              // `detailRow` falls back to its click-time snapshot once the tool's last account
              // is gone, so an emptied tool must close the sheet rather than show deleted rows.
              const open = openDetailRow.current
              const emptiesTool = !open || connections(open).length <= targets.length
              if (closeSheetOnDelete || emptiesTool) setDetailApp(null)
            },
          },
        })
        return
      }

      const enabling = action === 'enable'
      const label = enabling ? 'Enable' : 'Disable'
      setPopupState({
        open: true,
        title: `${label} App`,
        description: `Are you sure you want to ${label.toLowerCase()} "${targets[0].name}"${accounts}?`,
        okButtonProps: {
          variant: enabling ? 'default' : 'destructive',
          onClick: () => {
            for (const target of targets) handleToggleState(target)
          },
        },
      })
    },
    [handleDelete, handleToggleState],
  )

  const handleRevokeAll = useCallback(
    (connection: ExternalAppRow) => {
      setPopupState({
        open: true,
        title: 'Revoke access',
        description: `Remove everyone else's access to "${connection.name}"? Only you will be able to use this connection.`,
        okButtonProps: {
          variant: 'destructive',
          onClick: () => {
            revokeAllAccess(connection)
            setPopupState({ open: false })
          },
        },
      })
    },
    [revokeAllAccess],
  )

  // Transform and filter data for grouped table
  const tableData = useMemo(() => {
    if (!apps || apps.length === 0) return []

    // Filter individual apps before grouping
    let filteredApps = apps
    if (statusFilter?.length > 0) {
      filteredApps = apps.filter((app) => statusFilter.includes(app.state?.toUpperCase()))
    }

    // Fill the owner's display name from the org user directory so the Owner avatar and the
    // account-identifier fallback render (the backend only knows the aom-id, not the name).
    const orderedApps = orderBy(filteredApps, ['name'], ['asc']).map((app) =>
      app.integrated_by?.id && userMap[app.integrated_by.id]
        ? { ...app, integrated_by: { ...app.integrated_by, name: userMap[app.integrated_by.id] } }
        : app,
    )
    return buildGroupedTableRows(orderedApps)
  }, [apps, statusFilter, userMap])

  // Re-read from `tableData` so the open sheet reflects actions taken inside it; the click-time
  // snapshot covers only an in-flight refetch.
  const detailRow = useMemo(() => {
    if (!detailApp) return null
    return tableData.find((row) => row.id === detailApp.id) ?? detailApp
  }, [detailApp, tableData])
  openDetailRow.current = detailRow

  useEffect(() => {
    const unbinds = [
      pubnubEventBus.on(PubnubEvents.INTEGRATION_SUCCESS, (data: IntegrationSuccessEventData) => {
        successToast({
          title: 'Success',
          description: data.message,
        })
        invalidateExternalAppQueries()
      }),
      pubnubEventBus.on(PubnubEvents.INTEGRATION_UPDATED, () => {
        invalidateExternalAppQueries()
      }),
      pubnubEventBus.on(PubnubEvents.INTEGRATION_ACCESS_UPDATED, () => {
        invalidateExternalAppQueries()
      }),
      pubnubEventBus.on(PubnubEvents.INTEGRATION_FAILED, (data: IntegrationErrorEventData) => {
        errorToast({
          title: 'Error',
          description: data.error,
        })
      }),
    ]
    return () => {
      unbinds.forEach((unbind) => unbind())
    }
  }, [])

  // Connecting happens in another tab, and queries do not refetch on focus app-wide.
  useEffect(() => {
    const refreshOnReturn = () => {
      if (document.visibilityState === 'visible') invalidateExternalAppQueries()
    }
    document.addEventListener('visibilitychange', refreshOnReturn)
    return () => document.removeEventListener('visibilitychange', refreshOnReturn)
  }, [])

  const handleNewIntegration = () => {
    setIsSideSheetOpen(true)
  }

  // for table cell classes
  const getTableCellClasses = (columnId: string, cellType: 'head' | 'body') => {
    const isHead = cellType === 'head'
    const baseClasses = isHead
      ? 'h-12 text-left align-middle font-jetbrains text-label uppercase text-tertiary [&:has([role=checkbox])]:pr-0'
      : 'py-2 align-middle font-inter text-s-400 [&:has([role=checkbox])]:pr-0 whitespace-nowrap'

    switch (columnId) {
      case 'expander':
        return cn(baseClasses, 'pl-4 pr-0')
      case 'name':
        return cn(baseClasses, 'px-0')
      default:
        return cn(baseClasses, 'px-4')
    }
  }

  // Table configuration
  const tableProps = {
    columns: getColumns(currentUser, {
      currentUser,
      canEdit: hasPermission(RbacModules.integration, RbacPermissionTypes.edit),
      canDelete: hasPermission(RbacModules.integration, RbacPermissionTypes.delete),
      onAction: handleToolAction,
    }),
    data: tableData,
    enableSorting: false,
    enableExpanding: false,
    enableHiding: false,
    enableColumnOrdering: false,
    enableColumnPinning: false,
    enablePagination: false,
    enableTopToolbar: true,
    enableBottomToolbar: true,
    showRowsPerPage: true,
    manualPagination: false,
    manualFiltering: false,
    initialState: {
      showGlobalFilter: true,
    },
    state: {
      isLoading,
    },
    idKey: 'id',
    cxTableSearchTextInputProps: {
      hideSearchIcon: true,
      placeholder: 'Search by app name...',
      inputClassName: '!w-64 m-0.5 font-inter text-s-400',
    },
    cxTableBodyCellProps: ({ column }: { column: { id: string } }) => ({
      className: getTableCellClasses(column.id, 'body'),
    }),
    cxTableHeadCellProps: ({ column }: { column: { id: string } }) => ({
      className: getTableCellClasses(column.id, 'head'),
    }),
    cxTableBodyRowProps: (params: {
      row: { original: ExternalAppRow; getCanExpand?: () => boolean; toggleExpanded?: () => void }
    }) => ({
      className: 'group hover:bg-hv1 cursor-pointer h-[48px]',
      onClick: (e: React.MouseEvent) => {
        // Don't handle row click if any popup is open or right-clicking for context menu
        if (isAnyPopupOpen || e.button === 2) {
          return
        }

        // A sheet, not row expansion: provider and connection rows cannot share one column set.
        if (params.row.original.isGroup) setDetailApp(params.row.original)
      },
    }),
    renderCustomFilter: () => (
      <FacetedFilter
        title="Status"
        options={integrationStatusOptions}
        value={statusFilter}
        onChange={(values) => setStatusFilter(values as string[])}
        multiSelect
        showResetButton
        className="w-auto min-w-[200px]"
      />
    ),
    renderToolbarInternalActions: () => (
      <ButtonWithPermission
        onClick={() => setIsSideSheetOpen(true)}
        module={RbacModules.integration}
        permissionType={RbacPermissionTypes.create}
      >
        <Plus className="h-4 w-4 mr-2" />
        Connect
      </ButtonWithPermission>
    ),
  }

  const EmptyState = ({ onNewIntegration }: { onNewIntegration: () => void }) => (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center rounded-lg bg-card py-20">
      <div className="flex flex-col items-start max-w-md">
        <IcIntegrationBadge className="mb-4" />
        <Typography variant="H3/500" className="mb-2">
          No tools connected yet
        </Typography>
        <Typography className="mb-6 text-secondary">
          Connect an app or your own MCP server, and its tools become available to your agents.
        </Typography>
        <ButtonWithPermission
          onClick={onNewIntegration}
          className="flex items-center w-full"
          module={RbacModules.integration}
          permissionType={RbacPermissionTypes.create}
          disabledOnNoPermission
          size="sm"
        >
          <Plus className="h-4 w-4 mr-2" />
          Connect
        </ButtonWithPermission>
      </div>
    </div>
  )

  return (
    <>
      {(isError || isRetrying) && (
        <div className="mb-4">
          <QueryError
            title="Couldn't load tool connections."
            testId="button-retry-tool-connections"
            isRetrying={isRetrying}
            onRetry={async () => {
              setIsRetrying(true)
              try {
                await refetch()
              } finally {
                setIsRetrying(false)
              }
            }}
          />
        </div>
      )}
      {(isError || isRetrying) && apps.length === 0 ? null : !isLoading && apps.length === 0 ? (
        <EmptyState onNewIntegration={handleNewIntegration} />
      ) : (
        <CxDataTable<ExternalAppRow> {...tableProps} />
      )}
      {isSideSheetOpen && <IntegrationSideSheet onClose={() => setIsSideSheetOpen(false)} />}
      {detailRow && (
        <IntegrationDetailSheet
          app={detailRow}
          currentUser={currentUser}
          onClose={() => setDetailApp(null)}
          onAccountAction={(action, connection) =>
            action === 'revoke'
              ? handleRevokeAll(connection)
              : handleToolAction(action, [connection], false)
          }
          onShare={(connection) => {
            setDetailApp(null)
            setSharingApp(connection)
          }}
          onCustomFields={(connection) => {
            setDetailApp(null)
            handleCustomFields(connection)
          }}
        />
      )}
      {sharingApp && (
        <ShareConnectionButton
          app={sharingApp}
          open
          onOpenChange={(open) => {
            if (!open) setSharingApp(null)
          }}
        />
      )}
      <ConfirmPopup {...popupState} setOpen={(open) => setPopupState({ ...popupState, open })} />
      {customFieldsApp && (
        <CustomFieldsDialog
          app={customFieldsApp}
          open={!!customFieldsApp}
          onOpenChange={(open) => !open && setCustomFieldsApp(null)}
        />
      )}
    </>
  )
}

export default ExternalApps
